"use client";

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Single-line horizontal scroller with fade + chevron buttons that appear only
 * when the content overflows on that side (the pinned-properties row).
 */
export function HorizontalScrollStrip({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    el.addEventListener("scroll", update, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", update);
    };
  }, [update, children]);

  const nudge = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.6), behavior: "smooth" });
  };

  // mask, not a colored overlay: needs no theme color; 15% at the edge keeps the cut-off item hinted
  const FADE = 120;
  const EDGE = "rgba(0,0,0,0.15)";
  const maskStops = [
    canLeft ? `${EDGE}, black ${FADE}px` : "black",
    canRight ? `black calc(100% - ${FADE}px), ${EDGE}` : "black",
  ].join(", ");
  const maskImage = canLeft || canRight ? `linear-gradient(to right, ${maskStops})` : undefined;

  const btn = "absolute top-0 bottom-0 z-10 flex w-8 items-center text-sec hover:text-foreground";

  return (
    <div className={`relative min-w-0 ${className}`}>
      {canLeft && (
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => nudge(-1)}
          className={`${btn} left-0 justify-center`}
        >
          <ChevronLeft size={16} />
        </button>
      )}
      <div
        ref={scrollRef}
        className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={maskImage ? { maskImage, WebkitMaskImage: maskImage } : undefined}
      >
        {children}
      </div>
      {canRight && (
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => nudge(1)}
          className={`${btn} right-0 justify-center`}
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}
